import { useContext } from "react";
import PropTypes from "prop-types";
import sld from "../../images/sld/Rectangle.svg";
import Linebar from "../../components/GRIDCONNECTED/Linebar";
import embLabels from "../../config/emb-labels-en.json";
import { DesignAssetContext } from "../CustomHooks/ContextApi";
import { uniqueId } from "lodash";
import Ecc from "../../components/ETOIslandable/Ecc";
import Busbar from "../../components/ETOIslandable/Busbar";
import Genset from "../../components/ETOIslandable/Genset";
import Battery from "../../components/ETOIslandable/Battery";
import PV1 from "../../components/ETOIslandable/PV1";
import PV2 from "../../components/ETOIslandable/PV2";
import PV3 from "../../components/ETOIslandable/PV3";
import Ev from "../../components/ETOIslandable/Ev";
import Utility from "../../components/ETOIslandable/Utility";
import LoadPvBess from "../../components/GensetPvBess/LoadPvBess";
import GroundingTransformer from "../../components/ReusableSvg/GroundingTransformer";

// Constants
const SWITCHBOARD_TYPES = {
  EXISTING: 'EXISTING_SWITCHBOARD',
  NEW: 'NEW_SWITCHBOARD',
};

const LOAD_SIDES = {
  LOAD: 'LOAD_SIDE',
};

const IslandableEtoSLD = () => {
  const {
    CBAssetList = [],
    CBEVList = [],
    CBLoadList = [],
    CBGTList,
    sldLoadCompleted,
    projectKind,
    switchboardType,
    count,
    loadSide,
  } = useContext(DesignAssetContext);

  // Helper function to check if all loads are configured
  const areAllConfigured = () => CBLoadList.every(item => item.uiData.configured);

  // Helper function to get component presence flags
  const getComponentFlags = () => ({
    isExisting: switchboardType === embLabels?.EXISTING_SWITCHBOARD,
    hasLoad: CBLoadList && CBLoadList.length > 0,
    hasEV: CBEVList && CBEVList.some((ev) => ev.uiData.status),
    hasGT: CBGTList && CBGTList.some((gt) => gt.uiData.status),
    derCount: count?.derCount || 0,
    evCount: count?.evCount || 0,
    gtCount: count?.gTCount || 0,
  });

  // Helper function to calculate main SVG x offset
  const getMainSvgXOffset = () => {
    const { isExisting, derCount } = getComponentFlags();
    if (isExisting) return 0;
    if (derCount === 6) return -80;
    if (derCount === 5) return -60;
    return "";
  };
  const isLoadSide = loadSide === "LOAD_SIDE";
  // Component positioning by DER count and switchboard type
  const POSITION_CONFIG = {
    EXISTING_SWITCHBOARD: {
      load: {
        base: { 6: 110, 5: 110, 4: 120, 3: 100, default: 0 },
        withEV: {
          6: `${isLoadSide ? 50 : 180}`,
          5: `${isLoadSide ? 90 : 170}`,
          4: `${isLoadSide ? 110 : 160}`,
          3: `${isLoadSide ? 100 : 150}`,
          default: 0
        },
        withGT: {
          6: 170,
          5: `${isLoadSide ? 120 : 170}`,
          4: `${isLoadSide ? 120 : 180}`,
          3: `${isLoadSide ? 100 : 160}`,
          default: 0
        },
        withEVandGT: {
          6: `${isLoadSide ? 70 : 200}`,
          5: `${isLoadSide ? 120 : 200}`,
          4: `${isLoadSide ? 160 : 220}`,
          3: `${isLoadSide ? 150 : 200}`,
          default: 0
        },
      },
      ev: {
        withGTandLoad: { 6: 80, 5: 120, 4: 100, 3: 90, default: 0 },
        withLoad: { 6: 70, 5: 90, 4: 40, 3: 50, default: 0 },
      },
      gt: {
        withLoad: {
          6: 110,
          5: 110,
          4: `${loadSide === "LOAD_SIDE" ? 30 : 35}`,
          3: 20,
          default: 0
        },
        withEVandLoad: { 6: 40, 5: 40, 4: -20, 3: -30, default: 0 },
      },
    },
    NEW_SWITCHBOARD: {
      load: {
        base: { 6: -65, 5: -65, 4: -50, 3: 0, default: 0 },
        withEV: { 6: -70, 5: -70, 4: -64, 3: -30, default: 0 },
        withGT: { 6: -18, 5: -18, 4: 10, 3: 20, default: 0 },
        withEVandGT: { 6: -43, 5: -10, 4: -30, 3: 10, default: 0 },
      },
      ev: {
        withLoad: { 6: 50, 5: 50, 4: 40, 3: -20, default: 0 },
        withGTandLoad: { 6: 100, 5: 100, 4: 80, 3: 33, default: 0 },
      },
      gt: {
        withLoad: { 6: 185, 5: 185, 4: 120, 3: 60, default: 0 },
        withEVandLoad: { 6: 182, 5: 130, 4: 80, 3: 30, default: 0 },
      },
    },
  };

  const getGTXPosition = () => {
    
    const { isExisting, hasLoad, hasEV, derCount } = getComponentFlags();

    // Get the appropriate config
    const config = isExisting
      ? POSITION_CONFIG.EXISTING_SWITCHBOARD.gt
      : POSITION_CONFIG.NEW_SWITCHBOARD.gt;

    // Scenario: Both Load and EV present
    if (hasLoad && hasEV && config.withEVandLoad) {
      return config.withEVandLoad[derCount] ?? config.withEVandLoad.default;
    }

    // Scenario: Only Load present (without EV)
    if (hasLoad && !hasEV && config.withLoad) {
      return config.withLoad[derCount] ?? config.withLoad.default;
    }

    // If GT exists but load isn't present, use withLoad position as fallback
    return config.withLoad?.[derCount] ?? config.withLoad?.default ?? 0;
  };

  const getEVXPosition = () => {
    const { isExisting, hasLoad, hasGT, derCount } = getComponentFlags();

    // Get the appropriate config based on switchboard type
    const config = isExisting
      ? POSITION_CONFIG.EXISTING_SWITCHBOARD.ev
      : POSITION_CONFIG.NEW_SWITCHBOARD.ev;

    // Scenario: Both GT and Load present
    if (hasGT && hasLoad && config.withGTandLoad) {
      return config.withGTandLoad[derCount] ?? config.withGTandLoad.default;
    }

    // Scenario: Load present without GT
    if (hasLoad && !hasGT && config.withLoad) {
      return config.withLoad[derCount] ?? config.withLoad.default;
    }

    // If EV exists but load isn't present, use withLoad position as fallback
    return config.withLoad?.[derCount] ?? config.withLoad?.default ?? 0;
  };

  const getLoadXPosition = () => {
    const { isExisting, derCount, hasEV, hasGT } = getComponentFlags();

    // Get the appropriate config based on switchboard type
    const config = isExisting
      ? POSITION_CONFIG.EXISTING_SWITCHBOARD.load
      : POSITION_CONFIG.NEW_SWITCHBOARD.load;

    // Apply scenario-based positioning for BOTH switchboard types

    // Scenario 4: Both GT and EV present
    if (hasGT && hasEV) {
      return config.withEVandGT[derCount] ?? config.withEVandGT.default;
    }

    // Scenario 2: EV without GT
    if (hasEV && !hasGT) {
      return config.withEV[derCount] ?? config.withEV.default;
    }

    // Scenario 3: GT without EV
    if (hasGT && !hasEV) {
      return config.withGT[derCount] ?? config.withGT.default;
    }

    // Scenario 1: Base position (no EV, no GT)
    return config.base[derCount] ?? config.base.default;
  };
  return (
    <div
      id="SLD-Image"
      className="col-sm-12 col-md-12 col-lg-12 col-xl-12 px-0"
    >
      <svg viewBox="0 0 1300 800">
        <g>
          <svg x="0" y="0" viewBox="0 0 1300 800">
            <g id="grid">
              <svg>
                <image href={sld} x="0" y="0" />
              </svg>
            </g>
            <svg x={getMainSvgXOffset()}>
              <g
                id="components-sld"
                style={sldLoadCompleted ? {} : { display: "none" }}
              >
                <Ecc height="200" />
                <svg x={switchboardType === SWITCHBOARD_TYPES.NEW ? "-35" : ""}>
                  <svg x={switchboardType === SWITCHBOARD_TYPES.NEW ? "30" : ""}>
                    <Busbar secondBusbar={true} />
                    {projectKind?.isMGaaSETOIsland && switchboardType === embLabels?.EXISTING_SWITCHBOARD && (
                      <Linebar key={uniqueId()} />
                    )}
                  </svg>
                  <Utility />
                  {CBAssetList[1].uiData.status && <Genset />}
                  {CBAssetList[2].uiData.status && <Battery />}
                  {CBAssetList[3].uiData.status && <PV1 />}
                  {CBAssetList[4].uiData.status && <PV2 />}
                  {CBAssetList[5].uiData.status && <PV3 />}
                </svg>

                {/* Load Component - Always Present */}
                <svg x={getLoadXPosition()} y="0" id="load-container">
                  {CBLoadList.length > 0 && (
                    <LoadPvBess
                      loadLength={count}
                      configured={areAllConfigured()}
                      isEVThere={CBEVList?.[0].uiData.status}
                    />
                  )}
                </svg>

                {/* EV Components - Conditional Rendering */}
                {CBEVList.some((ev) => ev.uiData.status) && (
                  <svg x={getEVXPosition()} y="0" id="ev-container">
                    {CBEVList.map(
                      (cbData, key) =>
                        cbData.uiData.status && (
                          <Ev key={cbData.configData.name} index={key} cbHeight="100"/>
                        )
                    )}
                  </svg>
                )}

                {/* Grounding Transformer Components - Conditional Rendering */}
                {CBGTList.some((ev) => ev.uiData.status) && (
                  <svg x={getGTXPosition()} y="0" id="gt-container">
                    {CBGTList.map(
                      (cbData, key) =>
                        cbData.uiData.status && (
                          <GroundingTransformer key={cbData.configData.name} index={key} />
                        )
                    )}
                  </svg>
                )}
              </g>
            </svg>
          </svg>
        </g>
      </svg>
    </div>
  );
};

/**
 * IslandableEtoSLD Component
 * Renders a Single Line Diagram (SLD) for an Islandable ETO system
 * with proper alignment and conditional rendering of components.
 *
 * Components rendered:
 * - LoadPvBess: Always present (required)
 * - Ev: Conditionally rendered based on CBEVList status
 * - GroundingTransformer: Conditionally rendered based on CBGTList status
 */
IslandableEtoSLD.propTypes = {};

IslandableEtoSLD.displayName = "IslandableEtoSLD";

export default IslandableEtoSLD;
